import React from 'react';
import Transition from 'react-transition-group/Transition';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  getUiState,
  getFeatures,
} from 'web-selectors';

import {
  uiActions,
} from 'web-actions';

import {
  Flex,
} from 'web-styled';

import {
  getTourSteps,
} from './tour_data';

import {
  calculateOnboardPercentage,
} from './onboard_data';

import Tour from './Tour';
import Tables from './Tables';
import Indicators from './Indicators';
import Onboarding from './Onboarding';

const duration = 800;

type TransitionState = 'entering' | 'entered';
const transitionStyles = {
  entering: { display: 'none' },
  entered: { display: 'block' },
};

const mapState = (state) => ({
  profile: coreSelectors.getProfile(state),
  features: getFeatures(state),
  workoutEvents: coreSelectors.getEventsGroupByBeginsAt()(state),
  missedWorkouts: coreSelectors.getMissedWorkouts()(state),
  clientsPerformance: coreSelectors.getClientsPerformance(state),
  firstWorkout: coreSelectors.getFirstWorkout(state),
  firstExerciseFolder: coreSelectors.getExerciseFolder(state),
  ...getUiState([
    'namespace',
    'openedTooltips',
  ])(state),
});

const Pro = () => {
  const {
    profile,
    features,
    namespace,
    firstWorkout,
    workoutEvents,
    missedWorkouts,
    openedTooltips,
    firstExerciseFolder,
    clientsPerformance,
  } = useSelector(mapState, shallowEqual);

  const dispatch = useDispatch();

  const userSteps = profile.user_settings.tutorial_steps;
  const videoSeen = profile.user_settings.web_onboarding_video_seen;
  const percentage = calculateOnboardPercentage(namespace.symbol, userSteps);
  const tourSteps = getTourSteps(userSteps);
  const canShowTour = namespace.symbol !== 'ideafit' && videoSeen && tourSteps.some(s => !s.isCompleted);

  const handleUpdateSteps = (steps: string[]) => {
    dispatch(
      coreActions.requestMarkTutotialStepsComplite({
        steps,
      }),
    );
  };

  const updateTooltips = (tips: any) => {
    dispatch(
      uiActions.setUiState({
        openedTooltips: tips,
      }),
    );
  };

  const handleGoal = (current: string) => {
    switch (current) {
      case 'add_client':
        if (openedTooltips.findIndex(el => el.key === 'Clients') === -1) {
          updateTooltips(
            [
              {
                key: 'clients',
              },
              {
                key: 'add_client',
              },
            ],
          );
        }
        break;
      case 'add_exercise':
        if (openedTooltips.findIndex(el => el.key === 'Exercises') === -1) {
          updateTooltips(
            [
              {
                key: 'Exercises',
              },
              {
                key: firstExerciseFolder?.id,
                label: 'Select a folder.',
                nextKey: 'add_exercises',
                isFolder: true,
              },
            ],
          );
        }
        break;
      case 'assign_workout':
        if (openedTooltips.findIndex(el => el.key === 'assign') === -1) {
          updateTooltips(
            [
              {
                key: 'Workout Templates',
              },
              {
                key: firstWorkout?.id,
                label: 'Select a Workout Template.',
              },
              {
                key: 'assign',
              },
              {
                key: 'choose_client',
              },
            ],
          );
        }
        break;
      case 'build_your_own_workout':
        if (openedTooltips.findIndex(el => el.key === 'add_workoutTemplates') === -1) {
          updateTooltips(
            [
              {
                key: 'Workout Templates',
              },
              {
                key: 'add_workoutTemplates',
              },
              {
                key: 'edit_workout_button',
              },
              {
                key: 'exercise_property',
              },
            ],
          );
        }
        break;
      case 'perform_workout':
        break;
      case 'review_documents':
        if (openedTooltips.findIndex(el => el.key === 'Documents') === -1) {
          updateTooltips(
            [
              {
                key: 'Documents',
              },
              {
                key: 'HTS Manual',
              },
              {
                key: 'view_document',
              },
            ],
          );
        }
        break;
      case 'got_questions':
        handleUpdateSteps([
          'add_client',
          'add_exercise',
          'assign_workout',
          'build_your_own_workout',
          'perform_workout',
          'review_documents',
          'got_questions',
        ]);
        break;
      default:
        break;
    }
  };

  return (
    <Flex
      column
      spacing={55}
      padding="41px 23px 54px"
      flex="1 1 0"
    >
      <Transition
        in={percentage === 100}
        timeout={duration}
        unmountOnExit
      >
        {(state: TransitionState) => (
          <div style={{
            width: '100%',
            ...transitionStyles[state],
          }}
          >
            <Indicators
              data={profile.dashboard || {}}
              features={features}
            />
          </div>
        )}
      </Transition>
      <Transition
        in={percentage !== 100}
        timeout={duration}
        unmountOnExit
      >
        {(state: TransitionState) => (
          <div style={{
            width: '100%',
            ...transitionStyles[state],
          }}
          >
            <Onboarding
              profile={profile}
              namespace={namespace}
              percentage={percentage}
              onGoal={handleGoal}
            />
          </div>
        )}
      </Transition>
      <Tables
        features={features}
        workoutEvents={workoutEvents}
        missedWorkouts={missedWorkouts}
        clientsPerformance={clientsPerformance}
      />
      {canShowTour && (
        <Tour
          tourSteps={tourSteps}
          handleOnPrev={(step) => {
            handleUpdateSteps(userSteps.filter(s => s !== step));
          }}
          handleOnNext={(step) => {
            handleUpdateSteps([step]);
          }}
        />
      )}
    </Flex>
  );
};

export default Pro;
